import { AfterViewInit, Component, OnInit, ViewChild } from "@angular/core";
import { ListingService } from "@app/services/listing.service";
import { Listing } from "@app/data/listing";
import { DropzoneConfigInterface } from "ngx-dropzone-wrapper";
import { UntypedFormControl } from "@angular/forms";
import { UntypedFormGroup } from "@angular/forms";
import { UntypedFormBuilder } from "@angular/forms";
import { Page } from "@app/data/page";
import * as _ from "underscore";

import { Subject, debounceTime, takeUntil } from "rxjs";
import { Output } from "@angular/core";
import { Input } from "@angular/core";
import { EventEmitter } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ElementRef } from "@angular/core";

export const SMALL_INVENTORY_LIMIT: number = 10;
export const INVENTORY_LIMIT: number = 20;
const SAMPLES_COLORS = ["Red", "Blue", "Yellow", "Green", "Purple"];

@Component({
  template: `
    <textarea
      class="form-control"
      [froalaEditor]="froalaOptions"
      [(ngModel)]="html"
    >
    </textarea>
  `,
  selector: "inline-froala",
})
export class InlineFroalaComponent {
  froalaOptions: Object = {
    key: "RB3J4B17C10iB6E5E4H5I2I3A6B6C5C4C-11NGNe1IODMGYNSFKV==",
    toolbarButtons: ["bold", "italic", "underline", "|", "html"],
    iconsTemplate: "font_awesome_5",
    immediateAngularModelUpdate: true,
    charCounterCount: false,
    fontFamilySelection: true,
    imageStyles: {},
    placeholderText: "",
    toolbarInline: true,
    toolbarVisibleWithoutSelection: true,
  };

  _html: string = "";

  public set html(html: string) {
    this._html = html;
    this.htmlChange.emit(html);
    this.onSave.emit();
  }

  @Input()
  public get html(): string {
    return this._html;
  }

  @Output()
  htmlChange: EventEmitter<any> = new EventEmitter();

  @Output()
  onSave: EventEmitter<any> = new EventEmitter();
}

@Component({
  templateUrl: "./listings-table.component.html",
  selector: "listings-table",
})
export class ListingsTableComponent implements OnInit, AfterViewInit {
  @Input()
  public checkboxable: boolean = false;

  descriptionEditing: boolean[] = [];
  @ViewChild("mydatatable")
  mydatatable: any;
  isLoading: boolean = false;
  isRemovingProducts: boolean = false;
  @Input()
  canAddProducts: boolean = false;
  @Input()
  type: string = "inventory"; // type can be inventory or order sheet

  _rowsSelected = [];
  @Input()
  page = new Page();
  cache: any = {};

  @Output()
  onRefresh: EventEmitter<any> = new EventEmitter();

  editedRow: any;
  public _keywords: string = "";
  @Output()
  keywordsChange: EventEmitter<string> = new EventEmitter();

  @Input()
  public get keywords(): string {
    return this._keywords;
  }

  public set keywords(keywords: string) {
    this._keywords = keywords;
    this.keywordsChange.emit(keywords);
  }

  @Output()
  onKeywords: EventEmitter<any> = new EventEmitter();
  updateSubject: Subject<Listing> = new Subject<Listing>();

  @Input()
  rows = new Array<Listing>();
  keywordsFormControl = new UntypedFormControl();
  formGroup: UntypedFormGroup;
  editingColumn: string;

  @ViewChild("topScrollbar") topScrollbar: ElementRef;
  @ViewChild("scroller") scroller: ElementRef;
  @ViewChild("datatableScroll") tableScroller: ElementRef;

  dropzoneConfig: DropzoneConfigInterface = {
    url: "api/v2/files",
    maxFilesize: 2,
    acceptedFiles: "image/*",
  };
  listingBeingRemoved;
  isAddingListing = false;
  hideNav: boolean;
  destroy$ = new Subject<void>();

  constructor(
    private listingService: ListingService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      keywords: "",
    });

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.hideNav = !!params.hideNav;
      });
    this.updateSubject
      .pipe(
        debounceTime(500) // Apply debounceTime using pipe
      )
      .subscribe({
        next: (listing) => {
          if (this.type === "inventory") {
            delete listing.__v;
            if (listing.images.length > 0) {
              listing.main_image = listing.images[0];
            }
            this.listingService.update(listing).then((newListing) => {
              this.rows[this.rows.indexOf(listing)] = newListing;
            });
          }
        },
      });

    this.formGroup
      .get("keywords")
      ?.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((newValue) => {
        this.onRefresh.emit(newValue);
      });

    this.setPage({ offset: 0 });
  }

  ngAfterViewInit(): void {
    this.scroller.nativeElement.style.width =
      this.tableScroller.nativeElement.scrollWidth + "px";

    this.topScrollbar.nativeElement.addEventListener("scroll", () => {
      this.tableScroller.nativeElement.scrollLeft =
        this.topScrollbar.nativeElement.scrollLeft;
    });

    this.tableScroller.nativeElement.addEventListener("scroll", () => {
      this.topScrollbar.nativeElement.scrollLeft =
        this.tableScroller.nativeElement.scrollLeft;
    });
  }

  @Input()
  get rowsSelected(): any[] {
    return this._rowsSelected;
  }

  @Output()
  rowsSelectedChange: EventEmitter<any> = new EventEmitter();

  set rowsSelected(rowsSelected: any[]) {
    this._rowsSelected = rowsSelected;
    this.rowsSelectedChange.emit(this._rowsSelected);
  }

  setPage(pageInfo) {
    this.page.pageNumber = pageInfo.offset;
    this.page.size = INVENTORY_LIMIT;
    this.onRefresh.emit();
  }

  startEditing(row: any, column: string) {
    this.editedRow = row;
    this.editingColumn = column;
  }

  onUploadSuccess($event, rowIndex) {
    this.rows[rowIndex].images.push($event[1].url);
    this.save(this.rows[rowIndex]);
  }

  public addListing() {
    this.isAddingListing = true;
    this.listingService.create().then((listing) => {
      this.isAddingListing = false;
      let rows = [...this.rows];
      rows.splice(0, 0, listing);
      this.rows = rows;
      this.page.size = this.rows.length;
      this.page.totalElements++;
      this.mydatatable.recalculate();
    });
  }

  addVariation(listing) {
    listing.variations = listing.variations || [];
    var variant = { title: "Colors", options: [] };
    this.addOption(listing, variant.options);
    this.addOption(listing, variant.options);
    listing.variations.push(variant);
    this.save(listing);
  }

  removeImage(listing, images, image) {
    images.splice(images.indexOf(image), 1);
    this.save(listing);
  }

  removeVariation(listing, variation) {
    listing.variations.splice(listing.variations.indexOf(variation), 1);
    this.save(listing);
  }

  removeOption(listing, variation, option) {
    variation.options.splice(variation.options.indexOf(option), 1);
    if (variation.options.length === 0) {
      this.removeVariation(listing, variation);
    } else {
      this.save(listing);
    }
  }

  removeListing(listing) {
    this.listingBeingRemoved = listing;
    if (this.type === "inventory") {
      this.listingService.delete(listing.id).then(() => {
        this.listingBeingRemoved = null;
        let rows = [...this.rows];
        rows.splice(this.rows.indexOf(listing), 1);
        this.rows = rows;
        this.page.size = this.rows.length;
      });
    }
  }

  removeListings() {
    this.isRemovingProducts = true;
    this.listingService
      .deleteBulk(
        _.map(this.rowsSelected, (row) => {
          return row.id;
        })
      )
      .then(() => {
        this.isRemovingProducts = false;
        this.rowsSelected.splice(0, this.rowsSelected.length);
        this.onRefresh.emit(null);
      });
  }

  addOption(listing, options) {
    options.push({
      title: SAMPLES_COLORS[options.length % SAMPLES_COLORS.length],
      sku: "SKU " + (options.length + 1),
      barcode: "10293209" + options.length,
      price: listing.price || 9.99,
      suggested_retail_price: listing.suggested_retail_price,
      stock: listing.stock || 0,
    });
    this.save(listing);
  }

  onSelect({ selected }) {
    this.rowsSelected.splice(0, this.rowsSelected.length);
    this.rowsSelected.push(...selected);
  }

  save(listing) {
    this.updateSubject.next(listing);
  }
  /**
   * The footer triggered a page event.
   */
  onFooterPage(event: any) {
    this.page.pageNumber = event.page - 1;
    this.onRefresh.emit();
  }

  protected addBulkPrice(listing) {
    listing.bulk_prices = listing.bulk_prices || [];
    var min = 10;
    var max = 19;
    if (listing.bulk_prices.length > 0) {
      var lastPrice = listing.bulk_prices[listing.bulk_prices.length - 1];
      min = lastPrice.max_quantity + 1;
      max = min + (lastPrice.max_quantity - lastPrice.min_quantity);
    }
    listing.bulk_prices.push({
      min_quantity: min,
      max_quantity: max,
      type: "percentage_discount",
      amount: 50,
    });
  }

  protected removeBulkPrice(listing, bulk_price) {
    listing.bulk_prices.splice(listing.bulk_prices.indexOf(bulk_price), 1);
  }

  protected applyCategory(source, field) {
    this.rows.forEach((listing) => {
      if (listing.category === source.category) {
        listing[field] = JSON.parse(JSON.stringify(source[field]));
      }
    });
  }

  protected applyAll(source, field) {
    this.rows.forEach((listing) => {
      listing[field] = JSON.parse(JSON.stringify(source[field]));
    });
  }

  protected isEditing(row: any, column: string): boolean {
    return row === this.editedRow && column === this.editingColumn;
  }

  protected getPriceTypeLabel(type: string): string {
    switch (type) {
      case "price_discount":
        return "Price OFF";
      case "percentage_discount":
        return "% OFF";
      case "fixed_price":
        return "Fixed Price";
    }
  }
}
